import { BottomSheetModal } from '@gorhom/bottom-sheet';
import {
  ChargeCardPaymentVaultSource,
  IChargeCardPaymentSource,
  Typenames,
} from '@varo/graphql-schema-types';
import {
  NativeStackHeaderProps,
  Stack,
  useLocalSearchParams,
  useRouter,
} from 'expo-router';
import useChargeCardPaymentOptions from 'features/believe/makePayment/hooks/useChargeCardPaymentOptions';
import { useMakePayment } from 'features/believe/makePayment/hooks/useMakePayment';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import BottomSheet from 'shared/components/vxl/BottomSheet/BottomSheet';
import Button from 'shared/components/vxl/Button/Button';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import RadioButton from 'shared/components/vxl/selection/RadioButton/RadioButton';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import BodySmText from 'shared/components/vxl/typography/primary/body/BodySmText';
import HyperlinkSmText from 'shared/components/vxl/typography/primary/hyperlink/HyperlinkSmText';
import TitleLgText from 'shared/components/vxl/typography/primary/title/TitleLgText';
import TitleMdText from 'shared/components/vxl/typography/primary/title/TitleMdText';
import { isAndroid, isWeb } from 'shared/constants/platform';
import ErrorScreen from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';

const PAY_BY_CHECK = 'paybycheck';
const CHECK = 'check';

const SelectPaymentScreen = () => {
  const router = useRouter();
  const { bottom } = useSafeAreaInsets();
  const { id } = useLocalSearchParams<{ id: string }>();
  const { result, loading, errorModel } = useChargeCardPaymentOptions();
  const [primaryButtonDisable, setPrimaryButtonDisable] = useState(true);
  const [selectedValue, setSelectedValue] =
    useState<IChargeCardPaymentSource>();

  const paymentOptions = useMakePayment();
  const bottomSheetRef = useRef<BottomSheetModal>(null);

  const { t: translatedText } = useTranslation('believe', {
    keyPrefix: 'makePayment',
  });

  const handleLinkPress = () => {
    bottomSheetRef?.current?.present();
  };
  const handleOkPress = () => {
    bottomSheetRef?.current?.dismiss();
  };

  const handleRadioPress = (value: IChargeCardPaymentSource) => {
    setPrimaryButtonDisable(false);
    setSelectedValue(value);
    paymentOptions?.setOptions(value.options);
    paymentOptions?.setIsSecuredAccount(
      (value as ChargeCardPaymentVaultSource)?.__typename ===
        Typenames.ChargeCardPaymentVaultSource,
    );
  };

  const handleNavigation = () => {
    if (selectedValue && selectedValue.id === PAY_BY_CHECK) {
      router.push('/believe/make-payment/payCheck');
    } else {
      router.push({
        pathname: '/believe/make-payment/schedulePayment',
        params: { accountId: id },
      });
    }
  };

  const getAnalyticsAndAttributes = () => {
    if (selectedValue && selectedValue.id === PAY_BY_CHECK) {
      return {
        analyticsId: CHECK,
        customAttributes: { payment_method: CHECK },
      };
    }
    return {
      analyticsId: 'bank_account',
      customAttributes: { payment_method: 'bank_account' },
    };
  };

  return (
    <View
      style={[
        styles.screen,
        { paddingBottom: bottom + (isWeb || isAndroid ? theme.spacing.m : 0) },
      ]}
    >
      <ScrollView contentContainerStyle={styles.scrollView}>
        <Stack.Screen
          options={{
            title: translatedText('makePayment'),
            headerBackVisible: true,
            headerShown: true,
            header: (props: NativeStackHeaderProps) => (
              <ExpoNavigationBar {...props} />
            ),
          }}
        />
        <ScreenTransitionOverlay
          isLoading={loading}
          LoadingComponent={<LoadingSpinner />}
          ErrorComponent={errorModel ? <ErrorScreen {...errorModel} /> : null}
        />
        <View>
          <TitleLgText numberOfLines={0}>
            {translatedText('howDoYouWantToPay')}
          </TitleLgText>
          {result.map((item, index) => {
            const showLink =
              item.__typename &&
              (item as ChargeCardPaymentVaultSource)?.__typename !==
                Typenames.ChargeCardPaymentVaultSource;
            return (
              <View key={item.id} style={styles.radioButtonContainer}>
                <RadioButton
                  key={item.title}
                  labelText={item.title}
                  checked={selectedValue?.id === item.id}
                  onPress={() => handleRadioPress(item)}
                  id={item.id}
                />
                <BodySmText style={styles.bodyTextStyle} numberOfLines={0}>
                  {`${item.description} `}
                  {showLink ? (
                    <HyperlinkSmText onPress={handleLinkPress}>
                      {translatedText('linkText')}
                    </HyperlinkSmText>
                  ) : null}
                </BodySmText>
                {index < result.length - 1 ? (
                  <View style={styles.hairLineStyle}>
                    <HairLineDivider />
                  </View>
                ) : null}
              </View>
            );
          })}
        </View>
        <View style={styles.bottomContainer}>
          <View style={styles.buttonStyle}>
            <Button
              label={translatedText('buttons.continue')}
              type="PRIMARY"
              isDisabled={primaryButtonDisable}
              {...getAnalyticsAndAttributes()}
              onPress={handleNavigation}
            />
          </View>
          <View style={styles.buttonStyle}>
            <Button
              label={translatedText('buttons.cancel')}
              type="SECONDARY"
              onPress={() => {
                router.back();
              }}
              analyticsId="paymentCancel"
            />
          </View>
        </View>
      </ScrollView>

      <BottomSheet ref={bottomSheetRef}>
        <View style={[styles.parentContainer, { paddingBottom: bottom }]}>
          <View>
            <TitleMdText>{translatedText('paymentProcessingTime')}</TitleMdText>
          </View>
          <View style={styles.childText}>
            <BodyBaseText>
              {translatedText('paymentProcessingSchedule')}
            </BodyBaseText>
            <BodyBaseText>{translatedText('paymentFundText')}</BodyBaseText>
          </View>
          <View style={styles.buttonContainer}>
            <Button
              label={translatedText('ok')}
              type="PRIMARY"
              onPress={handleOkPress}
            />
          </View>
        </View>
      </BottomSheet>
    </View>
  );
};

const styles = StyleSheet.create({
  bodyTextStyle: {
    paddingTop: theme.spacing.s,
  },
  bottomContainer: {
    flex: 1,
    justifyContent: 'flex-end',
  },
  buttonContainer: {
    paddingTop: theme.spacing.m,
  },
  buttonStyle: {
    marginTop: theme.spacing.m,
  },
  childText: {
    gap: theme.spacing.l,
  },
  hairLineStyle: {
    paddingTop: theme.spacing.m,
  },
  parentContainer: {
    gap: theme.spacing.m,
    paddingBottom: theme.spacing.m,
    paddingHorizontal: theme.spacing.m,
  },
  radioButtonContainer: {
    paddingTop: theme.spacing.m,
  },
  screen: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  scrollView: {
    flexGrow: 1,
    justifyContent: 'space-between',
    paddingHorizontal: theme.spacing.m,
    paddingTop: theme.spacing.m,
  },
});

export default SelectPaymentScreen;
